import { useEffect, useMemo, useState } from 'react';

import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import useEnsureCards from '../../hooks/useEnsureCards';
import useLinkedItems from '../../hooks/useLinkedItems';
import useUnitSystem from '../../hooks/useUnitSystem';
import { getUsersByIds } from '../../services/users';
import { readCount } from '../../utils/confirmationValues';
import { normalizeId } from '../../utils/ids';
import { createLookupResolver } from '../../utils/lookups';
import { markdownToPlainText } from '../../utils/markdown';
import { isEmptyUserValue, userIdsOf } from '../../utils/userElements';
import { userLabel } from '../users/UserChip';
import { elementLabel, formatValue, getChoiceMeta } from '../items/itemValueDisplay';
import { PIN_KIND } from '../../services/favorites';

/**
 * The values of a Home record tile (KPR-1127), read as the item's detail reads
 * them. Each value goes through `formatValue` (components/items/
 * itemValueDisplay), the platform's ONE reading of a stored value by its data
 * type, with the person's unit system; a reference through the same lookup
 * pool the detail resolves against (useLinkedItems + createLookupResolver); a
 * person by their name; rich text as its words. A blank reads as the detail's
 * em-dash. The API has already decided WHICH values the person may read
 * (`pin.shown`): a name outside it is never drawn.
 */

/** What a blank value reads as — the detail's empty state. */
export const EMPTY_VALUE = '—';

const isBlank = value => value === undefined || value === null || value === '' || (Array.isArray(value) && !value.length);

/**
 * One value as text, the way the detail formats it. `context`:
 * `{ unitSystem, resolveLookupValue, userName }`. '' for a blank.
 */
export function tileValueText(element, value, context = {}) {
	if (isBlank(value)) return '';
	switch (element?.dataType) {
		// The words, not the markup; one line in a cell.
		case 'rich-text':
			return markdownToPlainText(String(value)).replace(/\s+/g, ' ').trim();
		case 'text-large':
			return String(value).replace(/\s+/g, ' ').trim();
		// People by name (the detail draws their chips; a cell prints names).
		case 'user': {
			if (isEmptyUserValue(value)) return '';
			const ids = userIdsOf(value);
			if (!ids.length) return formatValue(element, value);
			return ids.map(id => (context.userName ? context.userName(id) : '') || 'Someone').join(', ');
		}
		// "Me too" with nobody yet reads as a blank, as on a tile.
		case 'confirmations':
			return readCount(value) > 0 ? String(formatValue(element, value)) : '';
		default: {
			const text = formatValue(element, value, { unitSystem: context.unitSystem, resolveLookupValue: context.resolveLookupValue });
			if (text === undefined || text === null) return '';
			return typeof text === 'string' ? text : String(text);
		}
	}
}

/** The dot a value carries: a choice's color, or a color value's own (user data, not theme). */
export function tileValueDot(element, value) {
	if (isBlank(value)) return null;
	if (element?.dataType === 'color' && typeof value === 'string') return value;
	return getChoiceMeta(element, value).color || null;
}

/**
 * The fields a tile draws: each name the API shows (`pin.shown`, in the
 * tile's order) with its element (the card's own definition, from the
 * card-definitions cache — the one the detail reads) and its value. A file
 * element (no text to show) is left out. `defs` null while the card is not
 * loaded yet: the fields are then `pending`.
 */
export function tileFields(pin, defs, context = {}) {
	const shown = Array.isArray(pin?.shown) ? pin.shown : [];
	const values = pin?.item?.elements && typeof pin.item.elements === 'object' ? pin.item.elements : {};
	const byName = new Map((defs || []).filter(el => el?.name).map(el => [el.name, el]));
	return shown
		.map((name) => {
			const element = byName.get(name) || null;
			if (element && (element.dataType === 'file' || element.access === 'hidden')) return null;
			const value = values[name];
			// A card the cache does not hold (yet): the name and the value as stored.
			const text = element
				? tileValueText(element, value, context)
				: (isBlank(value) || typeof value === 'object' ? '' : String(value));
			return {
				name,
				label: element ? elementLabel(element) : name,
				text,
				display: text || EMPTY_VALUE,
				dot: element ? tileValueDot(element, value) : null,
				pending: !defs,
			};
		})
		.filter(Boolean);
}

/**
 * What every tile on the strip formats with, gathered ONCE for the strip:
 * the card definitions (fetched if missing), the lookup pool for the
 * references the tiles show, the names of the people they show, and the
 * person's unit system. → `{ defsFor(cardId), context }`.
 */
export function useTileValueContext(pins) {
	const cards = useOptionalCardDefinitions();
	const getElementsForCard = cards?.getElementsForCard;
	const cardsById = cards?.cardsById;
	const cardsLoading = cards ? cards.loading : false;
	const unitSystem = useUnitSystem();
	const items = useMemo(() => (pins || [])
		.filter(pin => pin?.targetType === PIN_KIND.RECORD_TILE && pin.item?._id)
		.map(pin => pin.item), [pins]);
	useEnsureCards(items);
	const itemsById = useLinkedItems({
		items,
		getElementsForCard,
		enabled: typeof getElementsForCard === 'function' && !cardsLoading,
		resetKey: 'home-record-tiles',
	});
	const resolveLookupValue = useMemo(
		() => createLookupResolver({ itemsById, cardsById, getElementsForCard }),
		[itemsById, cardsById, getElementsForCard]
	);

	// The people the tiles show, by name — one read for all of them.
	const userIds = useMemo(() => {
		if (typeof getElementsForCard !== 'function') return [];
		const ids = new Set();
		items.forEach((item) => {
			(getElementsForCard(item.card_id) || []).forEach((el) => {
				if (el?.dataType !== 'user') return;
				userIdsOf(item.elements?.[el.name]).forEach(id => ids.add(normalizeId(id)));
			});
		});
		return [...ids].filter(Boolean).sort();
	}, [items, getElementsForCard]);
	const userKey = userIds.join(',');
	const [names, setNames] = useState({});
	useEffect(() => {
		if (!userKey) return undefined;
		let active = true;
		getUsersByIds(userKey.split(','))
			.then((users) => {
				if (!active) return;
				const next = {};
				(users || []).forEach((user) => { next[normalizeId(user?._id)] = userLabel(user); });
				setNames(next);
			})
			// A name that cannot be read reads as "Someone", never an id.
			.catch(() => {});
		return () => { active = false; };
	}, [userKey]);

	const context = useMemo(() => ({
		unitSystem,
		resolveLookupValue,
		userName: id => names[normalizeId(id)] || '',
	}), [unitSystem, resolveLookupValue, names]);

	const defsFor = useMemo(() => (cardId) => {
		if (typeof getElementsForCard !== 'function') return null;
		const id = normalizeId(cardId);
		if (!id || !cardsById?.[id]) return cardsLoading ? null : [];
		return getElementsForCard(id) || [];
	}, [getElementsForCard, cardsById, cardsLoading]);

	return { defsFor, context };
}
